import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pastOrdersApi } from '../api/pastOrders';
import { fetchCustomerUnifiedOrders, orderPath } from '../lib/customerOrderFeed';
import { getCustomerSession } from '../lib/customerSession';
import { formatDeliveryCodeDisplay } from '../lib/deliveryConfirmationCode';
import { filterOrders, statusLabel } from '../lib/orderFilters';
import { pastDay, pastFare, usePastOrders } from '../lib/usePastOrders';
import './customerAccount.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
];

function badgeClass(status, statusText) {
  const text = String(statusText ?? statusLabel(status)).toLowerCase();
  if (status === 'cancelled' || text.includes('cancel')) return 'oh-badg oh-badg--cancelled';
  if (status === 'delivered' || text.includes('delivered')) return 'oh-badg oh-badg--delivered';
  if (text.includes('placed') || text.includes('pending') || text.includes('awaiting')) {
    return 'oh-badg oh-badg--placed';
  }
  if (status === 'transit' || status === 'active') return 'oh-badg oh-badg--active';
  return 'oh-badg oh-badg--active';
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16M21 21v-5h-5"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The client's orders: InGo's deliveries, then the orders from the app before
 * InGo, kept as history (InGo ADR 0034), shown under the list and filtered
 * with it. An old order opens nothing: it is a record, not a delivery.
 */
export default function OrderHistoryPage({ pastOrders = pastOrdersApi }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const old = usePastOrders(pastOrders.client);
  const [liveOrders, setLiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const session = getCustomerSession();
    const { orders, error } = await fetchCustomerUnifiedOrders(session);
    setLiveOrders(orders);
    if (error) setLoadError(error);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      load();
    }, 40000);
    return () => window.clearInterval(id);
  }, [load]);

  const list = useMemo(() => filterOrders(liveOrders, filter), [liveOrders, filter]);
  // Every old order ended, delivered or cancelled: none is active.
  const oldList = useMemo(
    () => (filter === 'all' ? old.orders : old.orders.filter((o) => o.status === filter)),
    [old.orders, filter],
  );

  // Every order is a delivery: booking again starts a new one.
  const onReorder = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      navigate('/request-delivery');
    },
    [navigate],
  );

  return (
    <div className="cust cust--orders">
      <header className="oh-nav">
        <h1 className="oh-nav__title">My Orders</h1>
        <button
          type="button"
          className="oh-nav__refresh"
          aria-label="Refresh orders"
          title="Refresh"
          onClick={() => load()}
          disabled={loading}
        >
          <RefreshIcon />
        </button>
      </header>

      <div className="oh-main">
        {loadError ? (
          <p className="oh-empty oh-empty--error" role="alert">
            {loadError}
          </p>
        ) : null}

        <div className="oh-tabs" role="tablist" aria-label="Order status">
          {FILTERS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={filter === t.id}
              className={filter === t.id ? 'oh-pill oh-pill--on' : 'oh-pill'}
              onClick={() => setFilter(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="oh-list">
          {loading ? (
            <p className="oh-empty">Loading your orders…</p>
          ) : list.length === 0 ? (
            oldList.length > 0 ? null : <p className="oh-empty">
              {liveOrders.length === 0
                ? 'No deliveries yet. Book one and it will show here.'
                : 'No orders in this list.'}
            </p>
          ) : (
            list.map((o) => (
              <div
                key={o.navKey}
                className="oh-card"
                role="link"
                tabIndex={0}
                onClick={() => navigate(orderPath(o))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(orderPath(o));
                  }
                }}
              >
                <div className="oh-card__top">
                  <h2 className="oh-card__id">{o.id}</h2>
                  <span className={badgeClass(o.status, o.statusText)}>
                    {o.statusText ?? statusLabel(o.status)}
                  </span>
                </div>
                {o.subtitle ? <p className="oh-card__sub">{o.subtitle}</p> : null}
                {o.deliveryConfirmationCode &&
                o.status !== 'delivered' &&
                o.status !== 'cancelled' ? (
                  <div className="oh-code" aria-label="Delivery PIN">
                    <span className="oh-code__label">Delivery PIN</span>
                    <span className="oh-code__value">{formatDeliveryCodeDisplay(o.deliveryConfirmationCode)}</span>
                  </div>
                ) : null}
                {o.status === 'cancelled' && o.cancelReason ? (
                  <p className="oh-cancelReason" role="status">
                    {o.cancelledBy === 'driver' ? 'Driver cancelled' : 'Cancelled'}
                    {' — '}
                    {o.cancelReason}
                  </p>
                ) : null}
                <div className="oh-locs">
                  <span className="oh-locs__line" aria-hidden />
                  <div className="oh-addr">
                    <span className="oh-dot oh-dot--pickup" aria-hidden />
                    <span className="oh-addr__text">{o.from}</span>
                  </div>
                  <div className="oh-addr oh-addr--drop">
                    <span className="oh-dot oh-dot--drop" aria-hidden />
                    <span className="oh-addr__text">{o.to}</span>
                  </div>
                </div>
                <div className="oh-btm">
                  <span className="oh-date">{o.date}</span>
                  <span className="oh-pr">{o.price}</span>
                  <button type="button" className="oh-reorder" onClick={onReorder}>
                    Book again
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {oldList.length > 0 || old.error ? (
          <section aria-label="From the app before InGo">
            <h2 className="oh-past__title">From the app before InGo</h2>
            {old.error ? (
              <p className="oh-empty oh-empty--error" role="alert">
                {old.error}
              </p>
            ) : null}
            <div className="oh-list">
              {oldList.map((o) => (
                <div key={`past-${o.id}`} className="oh-card">
                  <div className="oh-card__top">
                    <h3 className="oh-card__id">{o.reference}</h3>
                    <span className={badgeClass(o.status, o.status_label)}>{o.status_label}</span>
                  </div>
                  {o.status === 'cancelled' && o.cancel_reason ? (
                    <p className="oh-cancelReason">Cancelled — {o.cancel_reason}</p>
                  ) : null}
                  <div className="oh-locs">
                    <span className="oh-locs__line" aria-hidden />
                    <div className="oh-addr">
                      <span className="oh-dot oh-dot--pickup" aria-hidden />
                      <span className="oh-addr__text">{o.pickup}</span>
                    </div>
                    <div className="oh-addr oh-addr--drop">
                      <span className="oh-dot oh-dot--drop" aria-hidden />
                      <span className="oh-addr__text">{o.dropoff}</span>
                    </div>
                  </div>
                  <div className="oh-btm">
                    <span className="oh-date">{pastDay(o)}</span>
                    <span className="oh-pr">{pastFare(o)}</span>
                  </div>
                </div>
              ))}
            </div>
            {old.next ? (
              <button type="button" className="oh-reorder" disabled={old.more} onClick={old.showOlder}>
                {old.more ? 'Loading…' : 'Show older'}
              </button>
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}
